/*
  Banner grid — mobile carousel.

  Uses the theme's bundled Swiper rather than a native scroll-snap track. A
  snap track moves in discrete jumps: the browser scrolls, then yanks to the
  nearest snap point, which reads as a stutter rather than a swipe. Swiper
  follows the finger and settles under its own easing, and it is what makes
  autoplay and clickable dots possible at all.

  Desktop renders a static grid, so the slider is built only below 750px and
  torn down again above it — Swiper measures 0 widths on a display:none host,
  so it must not stay alive while the carousel is hidden.
*/

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE = window.matchMedia('(max-width: 749px)');

  /*
    Swiper builds a loop by cloning slides, and it needs a full view's worth on
    each side to do it. A three-banner section at 1.16 per view does not have
    that, so Swiper quietly drops out of loop mode: the track runs to the last
    banner and rewinds instead of carrying on, and the peek disappears at both
    ends because there is nothing left to peek at. Repeating the real banners in
    the DOM first gives it enough to work with.

    Copies must not keep ids — the banners are plain links today, but a
    duplicated id would break the DOM the moment one gains a form or a label.
  */
  function padForLoop(wrapper, perView) {
    var originals = Array.prototype.slice.call(wrapper.children);
    if (!originals.length) return;

    var required = Math.ceil(perView) * 3;
    if (originals.length >= required) return;

    var index = 0;
    while (wrapper.children.length < required) {
      var copy = originals[index % originals.length].cloneNode(true);
      copy.removeAttribute('id');
      copy.querySelectorAll('[id]').forEach(function (node) {
        node.removeAttribute('id');
      });
      copy.setAttribute('data-banner-grid-duplicate', 'true');
      wrapper.appendChild(copy);
      index += 1;
    }
  }

  function buildDot(root, host, index) {
    var dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'banner-grid__dot';
    dot.setAttribute('aria-label', 'Go to banner ' + (index + 1));
    dot.addEventListener('click', function () {
      var s = root.swiperInstance;
      if (!s) return;
      // slideToLoop takes a real index, so it lands on the right banner even
      // when the track is sitting on one of the padded copies.
      if (s.params.loop) s.slideToLoop(index);
      else s.slideTo(index);
    });
    host.appendChild(dot);
    return dot;
  }

  function build(root) {
    if (root.swiperInstance) return;

    var container = root.querySelector('.swiper');
    if (!container || typeof window.Swiper === 'undefined') return;

    /*
      Counted before init: in loop mode Swiper adds duplicate slides, so asking
      afterwards would over-count and shrink the progress bar.
    */
    var count = container.querySelectorAll('.swiper-slide').length;
    if (!count) return;

    var perView = parseFloat(root.getAttribute('data-per-view')) || 1;
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var delay = parseInt(root.getAttribute('data-autoplay-delay'), 10) || 4000;
    var loop = root.getAttribute('data-loop') !== 'false';

    var wrapper = container.querySelector('.swiper-wrapper');
    var willLoop = loop && count > 1;
    if (willLoop && wrapper) padForLoop(wrapper, perView);

    var options = {
      slidesPerView: perView,
      spaceBetween: parseInt(root.getAttribute('data-gap'), 10) || 12,
      /*
        Never centred. A centred slide is inset by half the leftover space on
        each side, which pushed the banner ~28px in from the screen — past the
        15px gutter the rest of the page lines up on. The track's padding now
        sets that edge instead, and the peek falls to the trailing side, which
        is how the category row above it already behaves.
      */
      centeredSlides: false,
      // One banner is not a carousel; looping it would clone a single slide.
      loop: willLoop,
      // Real duplicates are supplied above, so never pad the track with blanks.
      rewind: false,
      loopAddBlankSlides: false,
      loopAdditionalSlides: Math.ceil(perView),
      watchOverflow: true,
      grabCursor: true,
      /*
        Only when each banner keeps its own shape. Slides then differ in height,
        and the flex wrapper would otherwise stretch them all to the tallest,
        leaving dead space under the shorter ones. autoHeight sizes the wrapper
        to the active slide instead. Left off for a fixed ratio, where every
        slide already matches and the measuring would be wasted work.
      */
      autoHeight: root.getAttribute('data-auto-height') === 'true',
    };

    /*
      Dots are built here rather than by Swiper's pagination module: padding for
      the loop repeats the banners, so Swiper would render a bullet per COPY and
      show six dots for three banners. These are one per real banner, and the
      active one is derived with realIndex % count so every copy maps back to the
      banner it was cloned from.
    */
    var dotsEl = root.querySelector('[data-banner-grid-pagination]');
    var dots = [];
    if (dotsEl && count > 1) {
      dotsEl.textContent = '';
      for (var i = 0; i < count; i += 1) {
        dots.push(buildDot(root, dotsEl, i));
      }
    }

    var markDot = function (s) {
      if (!dots.length) return;
      var active = s.realIndex % count;
      dots.forEach(function (dot, i) {
        var on = i === active;
        dot.classList.toggle('banner-grid__dot--active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
    };

    // Reduced motion: never move the banners on their own.
    if (autoplay && count > 1 && !REDUCED.matches) {
      options.autoplay = {
        delay: delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    /*
      Painted from realIndex rather than Swiper's raw progress: in loop mode
      progress covers the duplicated track, so it would never reach either end.
    */
    var bar = root.querySelector('[data-scroll-slider-progress-bar]');
    var paint = null;
    if (bar) {
      paint = function (s) {
        var width = 100 / count;
        var real = s.realIndex % count;
        var step = count > 1 ? real / (count - 1) : 0;
        bar.style.width = width + '%';
        bar.style.transform = 'translateX(' + step * (count - 1) * 100 + '%)';
      };
    }

    var update = function (s) {
      if (paint) paint(s);
      markDot(s);
    };

    options.on = {
      init: update,
      realIndexChange: update,
      resize: update,
    };

    root.swiperInstance = new window.Swiper(container, options);
  }

  function teardown(root) {
    if (!root.swiperInstance) return;
    root.swiperInstance.destroy(true, true);
    root.swiperInstance = null;

    /*
      Drop the copies padForLoop added. Without this each breakpoint crossing
      pads an already padded track and the banner count runs away.
    */
    root.querySelectorAll('[data-banner-grid-duplicate]').forEach(function (el) {
      el.parentNode.removeChild(el);
    });

    var dotsEl = root.querySelector('[data-banner-grid-pagination]');
    if (dotsEl) dotsEl.textContent = '';
  }

  function sync(scope) {
    (scope || document).querySelectorAll('[data-banner-grid]').forEach(function (root) {
      if (MOBILE.matches) build(root);
      else teardown(root);
    });
  }

  function init() {
    sync();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Build or tear down as the breakpoint is crossed, not on every resize tick.
  if (MOBILE.addEventListener) MOBILE.addEventListener('change', function () { sync(); });
  else if (MOBILE.addListener) MOBILE.addListener(function () { sync(); });

  document.addEventListener('shopify:section:load', function (e) {
    sync(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-banner-grid]').forEach(teardown);
  });
})();
